import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCart } from '@/contexts/CartContext';
import { getProductInventory } from '@/lib/api';
import { getFirstImage } from '@/utils/imageUtils';
import { 
  ShoppingCart, Package, Loader2, Plus, Minus, Trash2,
  Image as ImageIcon, AlertCircle, Check, Warehouse
} from 'lucide-react';
import { toast } from 'sonner';

const ProductModal = ({ product, isOpen, onClose, isUSA = false }) => {
  const { addItem, loading: cartLoading } = useCart();
  const [loading, setLoading] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  
  // Current selection
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [currentQuantity, setCurrentQuantity] = useState(1);
  
  // Cart items list (multiple variant combinations)
  const [cartItems, setCartItems] = useState([]);

  // Unit label
  const unitLabel = useMemo(() => {
    const unitMap = {
      'pzas': isUSA ? 'pcs' : 'pzas',
      'Mt': isUSA ? 'm' : 'mts',
      'Kg': 'kg',
      'Yds': isUSA ? 'yd' : 'yds',
      'Lbs': 'lbs',
      'rollos': isUSA ? 'rolls' : 'rollos',
      'metros': isUSA ? 'm' : 'mts',
    };
    return unitMap[product?.unit] || product?.unit || 'pzas';
  }, [product?.unit, isUSA]);

  useEffect(() => {
    if (product && isOpen) {
      resetSelections();
    }
  }, [product, isOpen]);

  const resetSelections = () => {
    setSelectedColor('');
    setSelectedSize('');
    setSelectedModel('');
    setCurrentQuantity(1);
    setCartItems([]);
    setCurrentImage(0);
  };

  // Get inventory from product
  const inventory = product?.inventory || [];
  const hasInventory = inventory.length > 0;

  // Get available colors from inventory
  const getColors = () => {
    if (hasInventory) {
      return [...new Set(inventory.filter(i => i.color).map(i => i.color))];
    }
    return product?.variants?.color || [];
  };

  // Get available sizes from inventory
  const getSizes = () => {
    if (hasInventory) {
      return [...new Set(inventory.filter(i => i.size).map(i => i.size))];
    }
    return product?.variants?.size || product?.variants?.talla || [];
  };

  // Get available models from inventory
  const getModels = () => {
    if (hasInventory) {
      return [...new Set(inventory.filter(i => i.model).map(i => i.model))];
    }
    return product?.variants?.model || product?.variants?.modelo || [];
  };

  // Get stock for a specific variant combination
  const getVariantStock = (color, size, model) => {
    if (!hasInventory) return product?.stock_total || 999;
    
    const variant = inventory.find(i => 
      (!color || i.color === color) &&
      (!size || i.size === size) &&
      (!model || i.model === model)
    );
    return variant?.stock || 0;
  };

  // Get stock for selected color
  const getColorStock = (color) => {
    if (!hasInventory) return null;
    const colorItems = inventory.filter(i => i.color === color);
    return colorItems.reduce((sum, i) => sum + (i.stock || 0), 0);
  };

  const hasSimpleVariants = getColors().length > 0 || getSizes().length > 0 || getModels().length > 0;

  // Check if current selection is complete
  const isSelectionComplete = () => {
    if (!hasSimpleVariants) return true;
    const needsColor = getColors().length > 0;
    const needsSize = getSizes().length > 0;
    const needsModel = getModels().length > 0;
    
    if (needsColor && !selectedColor) return false;
    if (needsSize && !selectedSize) return false;
    if (needsModel && !selectedModel) return false;
    return true;
  };

  // Get current variant stock
  const currentVariantStock = useMemo(() => {
    if (!isSelectionComplete()) return null;
    return getVariantStock(selectedColor, selectedSize, selectedModel);
  }, [selectedColor, selectedSize, selectedModel, inventory]);

  // Get current variant summary
  const getCurrentVariantSummary = () => {
    const parts = [];
    if (selectedColor) parts.push(selectedColor);
    if (selectedSize) parts.push(selectedSize);
    if (selectedModel) parts.push(selectedModel);
    return parts.join(' / ') || 'Sin variante';
  };

  // Check if variant combination already in cart
  const isVariantInCart = () => {
    return cartItems.some(item => 
      item.color === selectedColor && 
      item.size === selectedSize && 
      item.model === selectedModel
    );
  };

  // Validate quantity against stock
  const validateQuantity = (qty) => {
    if (!hasInventory) return true;
    if (currentVariantStock === null) return true;
    return qty <= currentVariantStock;
  };

  // Add current selection to cart list
  const addToCartList = () => {
    if (!isSelectionComplete()) {
      toast.error(isUSA ? 'Please select all options' : 'Selecciona todas las opciones');
      return;
    }
    
    if (isVariantInCart()) {
      toast.error(isUSA ? 'This variant is already in the list' : 'Esta variante ya está en la lista');
      return;
    }

    // Validate stock
    if (hasInventory && currentVariantStock !== null && currentQuantity > currentVariantStock) {
      toast.error(isUSA 
        ? `Only ${currentVariantStock} ${unitLabel} available` 
        : `Solo hay ${currentVariantStock} ${unitLabel} disponibles`
      );
      return;
    }

    const newItem = {
      id: Date.now(),
      color: selectedColor,
      size: selectedSize,
      model: selectedModel,
      quantity: currentQuantity,
      stock: currentVariantStock,
      summary: getCurrentVariantSummary()
    };

    setCartItems([...cartItems, newItem]);
    
    // Reset for next selection
    setSelectedColor('');
    setSelectedSize('');
    setSelectedModel('');
    setCurrentQuantity(1);
    
    toast.success(isUSA ? 'Added to list' : 'Agregado a la lista');
  };

  // Remove item from cart list
  const removeFromCartList = (id) => {
    setCartItems(cartItems.filter(item => item.id !== id));
  };

  // Update quantity for item in list
  const updateItemQuantity = (id, newQty) => {
    const item = cartItems.find(i => i.id === id);
    if (item && hasInventory && item.stock !== null && newQty > item.stock) {
      toast.error(isUSA 
        ? `Only ${item.stock} ${unitLabel} available for ${item.summary}` 
        : `Solo hay ${item.stock} ${unitLabel} disponibles para ${item.summary}`
      );
      return;
    }
    setCartItems(cartItems.map(item => 
      item.id === id ? { ...item, quantity: Math.max(1, newQty) } : item
    ));
  };

  // Add all items to cart
  const handleAddAllToCart = async () => {
    let itemsToAdd = [...cartItems];
    
    if (itemsToAdd.length === 0 && isSelectionComplete()) {
      // Validate stock for direct add
      if (hasInventory && currentVariantStock !== null && currentQuantity > currentVariantStock) {
        toast.error(isUSA 
          ? `Only ${currentVariantStock} ${unitLabel} available` 
          : `Solo hay ${currentVariantStock} ${unitLabel} disponibles`
        );
        return;
      }
      
      itemsToAdd = [{
        id: Date.now(),
        color: selectedColor,
        size: selectedSize,
        model: selectedModel,
        quantity: currentQuantity,
        stock: currentVariantStock,
        summary: getCurrentVariantSummary()
      }];
    }

    if (itemsToAdd.length === 0) {
      toast.error(isUSA ? 'Add at least one variant' : 'Agrega al menos una variante');
      return;
    }

    // Add each item to cart
    let successCount = 0;
    for (const item of itemsToAdd) {
      const cartItem = {
        product_id: product.product_id,
        product_name: product.name,
        variant_id: null,
        variant_name: item.summary,
        variant_options: {
          color: item.color || null,
          size: item.size || null,
          model: item.model || null
        },
        quantity: item.quantity || 1,
        unit: product.unit || 'pzas',
        unit_price: product.price_visible ? product.price : null,
        image_url: getFirstImage(product.images),
        max_stock: item.stock // Pass stock info for validation
      };

      const success = await addItem(cartItem);
      if (success) successCount++;
    }

    if (successCount > 0) {
      toast.success(
        isUSA 
          ? `${successCount} item(s) added to cart` 
          : `${successCount} artículo(s) agregado(s) al carrito`
      );
      onClose();
    }
  };

  // Calculate total quantity in cart list
  const totalInList = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  if (!product) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-modal">
        <DialogHeader>
          <DialogTitle className="text-xl">{product.name}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Images */}
          <div className="space-y-3">
            <div className="aspect-square bg-slate-100 rounded-lg overflow-hidden">
              {product.images?.[currentImage] ? (
                <img 
                  src={product.images[currentImage]} 
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="w-20 h-20 text-slate-300" />
                </div>
              )}
            </div>
            
            {product.images?.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImage(idx)}
                    className={`w-16 h-16 rounded border-2 overflow-hidden flex-shrink-0 ${
                      idx === currentImage ? 'border-emerald-500' : 'border-transparent'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline">{product.category}</Badge>
              {product.stock_total > 0 && (
                <Badge className="bg-emerald-100 text-emerald-700">
                  <Package className="w-3 h-3 mr-1" />
                  {product.stock_total.toLocaleString()} {unitLabel} {isUSA ? 'in stock' : 'en stock'}
                </Badge>
              )}
            </div>
            
            <p className="text-slate-600">{product.description}</p>

            {/* Price */}
            {product.price_visible && product.price ? (
              <div className="text-2xl font-bold text-emerald-600">
                ${product.price.toLocaleString()} {product.currency || 'MXN'}
                <span className="text-sm font-normal text-slate-500">/{unitLabel}</span>
              </div>
            ) : (
              <p className="text-slate-500 italic">{isUSA ? 'Price on request' : 'Precio a consultar'}</p>
            )}

            {/* Inventory Summary - Show when inventory exists */}
            {hasInventory && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <Warehouse className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-blue-800">
                    {isUSA ? 'Available Inventory' : 'Inventario Disponible'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm max-h-32 overflow-y-auto">
                  {getColors().map(color => {
                    const stock = getColorStock(color);
                    return (
                      <div key={color} className={`flex justify-between p-1 rounded ${stock > 0 ? 'text-blue-700' : 'text-red-500'}`}>
                        <span>{color}</span>
                        <span className="font-medium">{stock.toLocaleString()} {unitLabel}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Simple variant selectors */}
            {hasSimpleVariants && (
              <div className="space-y-4 border rounded-lg p-4 bg-slate-50">
                <Label className="font-semibold text-slate-700">
                  {isUSA ? 'Select variants to add' : 'Selecciona variantes para agregar'}
                </Label>
                
                {/* Color selector with stock */}
                {getColors().length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-600">{isUSA ? 'Color' : 'Color'}</Label>
                    <div className="flex flex-wrap gap-2">
                      {getColors().map((color) => {
                        const colorStock = getColorStock(color);
                        const isOutOfStock = hasInventory && colorStock === 0;
                        return (
                          <button
                            key={color}
                            onClick={() => !isOutOfStock && setSelectedColor(color)}
                            disabled={isOutOfStock}
                            className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                              isOutOfStock
                                ? 'border-red-200 bg-red-50 text-red-400 cursor-not-allowed line-through'
                                : selectedColor === color
                                  ? 'bg-emerald-600 text-white border-emerald-600'
                                  : 'border-slate-300 hover:border-emerald-400 bg-white'
                            }`}
                            title={hasInventory ? `${colorStock} ${unitLabel} disponibles` : ''}
                          >
                            {color}
                            {hasInventory && (
                              <span className="ml-1 text-xs opacity-75">
                                ({colorStock})
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Size selector */}
                {getSizes().length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-600">{isUSA ? 'Size' : 'Talla'}</Label>
                    <div className="flex flex-wrap gap-2">
                      {getSizes().map((size) => (
                        <button
                          key={size}
                          onClick={() => setSelectedSize(size)}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                            selectedSize === size
                              ? 'bg-emerald-600 text-white border-emerald-600'
                              : 'border-slate-300 hover:border-emerald-400 bg-white'
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Model selector */}
                {getModels().length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-600">{isUSA ? 'Model' : 'Modelo'}</Label>
                    <div className="flex flex-wrap gap-2">
                      {getModels().map((model) => (
                        <button
                          key={model}
                          onClick={() => setSelectedModel(model)}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                            selectedModel === model
                              ? 'bg-emerald-600 text-white border-emerald-600'
                              : 'border-slate-300 hover:border-emerald-400 bg-white'
                          }`}
                        >
                          {model}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quantity for current variant */}
                <div className="flex items-center gap-4 pt-2">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">{isUSA ? 'Qty' : 'Cant'} ({unitLabel}):</Label>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setCurrentQuantity(Math.max(1, currentQuantity - 1))}
                    >
                      <Minus className="w-3 h-3" />
                    </Button>
                    <Input
                      type="number"
                      min="1"
                      max={currentVariantStock || undefined}
                      value={currentQuantity}
                      onChange={(e) => setCurrentQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-20 h-8 text-center"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setCurrentQuantity(currentQuantity + 1)}
                    >
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                  
                  <Button
                    onClick={addToCartList}
                    disabled={!isSelectionComplete()}
                    className="bg-blue-600 hover:bg-blue-700"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {isUSA ? 'Add variant' : 'Agregar variante'}
                  </Button>
                </div>

                {/* Current selection stock info */}
                {isSelectionComplete() && currentVariantStock !== null && (
                  <p className={`text-sm flex items-center gap-1 ${currentVariantStock > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    <Package className="w-4 h-4" />
                    {currentVariantStock > 0 
                      ? `${currentVariantStock.toLocaleString()} ${unitLabel} ${isUSA ? 'available for' : 'disponibles de'} ${getCurrentVariantSummary()}`
                      : (isUSA ? 'Out of stock' : 'Agotado')
                    }
                  </p>
                )}
              </div>
            )}

            {/* Cart items list */}
            {cartItems.length > 0 && (
              <div className="border rounded-lg p-3 bg-white space-y-2">
                <Label className="font-semibold text-emerald-700 flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4" />
                  {isUSA ? 'Items to add' : 'Artículos a agregar'} ({cartItems.length})
                </Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {cartItems.map((item) => (
                    <div key={item.id} className="flex items-center justify-between bg-slate-50 rounded p-2 text-sm">
                      <span className="font-medium">{item.summary}</span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => updateItemQuantity(item.id, item.quantity - 1)}
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="w-12 text-center">{item.quantity} {unitLabel}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => updateItemQuantity(item.id, item.quantity + 1)}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-red-500 hover:text-red-700"
                          onClick={() => removeFromCartList(item.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 text-right">
                  Total: {totalInList.toLocaleString()} {unitLabel}
                </p>
              </div>
            )}

            {/* Unit selector - only for products without variants */}
            {!hasSimpleVariants && (
              <div className="space-y-2">
                <Label>{isUSA ? 'Quantity' : 'Cantidad'} ({unitLabel})</Label>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={() => setCurrentQuantity(Math.max(1, currentQuantity - 1))}>
                    <Minus className="w-4 h-4" />
                  </Button>
                  <Input
                    type="number"
                    min="1"
                    value={currentQuantity}
                    onChange={(e) => setCurrentQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-24 text-center"
                  />
                  <Button variant="outline" size="icon" onClick={() => setCurrentQuantity(currentQuantity + 1)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                  <span className="text-slate-500 text-sm">{unitLabel}</span>
                </div>
              </div>
            )}

            {/* MOQ warning */}
            {product.moq && (currentQuantity < product.moq || totalInList + currentQuantity < product.moq) && (
              <p className="text-sm text-amber-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {isUSA ? `Minimum order: ${product.moq} ${unitLabel}` : `Pedido mínimo: ${product.moq} ${unitLabel}`}
              </p>
            )}

            {/* Add to cart button */}
            <Button
              onClick={handleAddAllToCart}
              disabled={cartLoading || (hasSimpleVariants && cartItems.length === 0 && !isSelectionComplete())}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              size="lg"
            >
              {cartLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ShoppingCart className="w-4 h-4 mr-2" />
              )}
              {cartItems.length > 0
                ? (isUSA ? `Add ${cartItems.length} variant(s) to cart` : `Agregar ${cartItems.length} variante(s) al carrito`)
                : (isUSA ? 'Add to cart' : 'Agregar al carrito')
              }
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductModal;
